import {
  configureStore,
  combineReducers,
  createListenerMiddleware,
  type ThunkAction,
  type Action,
  type Middleware,
} from '@reduxjs/toolkit'
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux'
import { useEffect } from 'react'
import merge from 'lodash/merge'
import { IS_PRODUCTION, CONFIG_SERVICE_KEY } from '@/config/constants'
import { getPreloadedState, persistState } from './persistStore'
import { broadcastState, listenToBroadcast } from './broadcast'
import {
  cookiesAndTermsSlice,
  cookiesAndTermsInitialState,
  safeMessagesListener,
  swapOrderListener,
  swapOrderStatusListener,
  txHistoryListener,
  txQueueListener,
  authListener,
  counterfactualSyncListener,
} from './slices'
import * as slices from './slices'
import * as hydrate from './useHydrateStore'
import { ofacApi } from '@/store/api/ofac'
import { safePassApi } from './api/safePass'
import { hypernativeApi } from '@safe-global/store/hypernative/hypernativeApi'
import { version as termsVersion } from '@/markdown/terms/version'
import { cgwClient, setBaseUrl } from '@safe-global/store/gateway/cgwClient'
import { GATEWAY_URL } from '@/config/gateway'
import { setupListeners } from '@reduxjs/toolkit/query'
import { migrateBatchTxs } from '@/services/ls-migration/batch'
import { apiSliceWithChainsConfig, chainsAdapter, chainsInitialState } from '@safe-global/store/gateway'
import type { Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
// Build-time chain data generated by scripts/fetch-chains.ts.
// Uses dynamic require because the file may not exist in CI environments
// that skip the build step (e.g. type-check, unit tests).
let staticChainsData: Chain[] = []
try {
  staticChainsData = require('@/config/__generated__/chains.json') as Chain[]
} catch {
  // File doesn't exist — fall back to empty array (runtime fetch will populate)
}

const rootReducer = combineReducers({
  [slices.safeInfoSlice.name]: slices.safeInfoSlice.reducer,
  [slices.sessionSlice.name]: slices.sessionSlice.reducer,
  [slices.txHistorySlice.name]: slices.txHistorySlice.reducer,
  [slices.txQueueSlice.name]: slices.txQueueSlice.reducer,
  [slices.swapOrderSlice.name]: slices.swapOrderSlice.reducer,
  [slices.addressBookSlice.name]: slices.addressBookSlice.reducer,
  [slices.notificationsSlice.name]: slices.notificationsSlice.reducer,
  [slices.pendingTxsSlice.name]: slices.pendingTxsSlice.reducer,
  [slices.addedSafesSlice.name]: slices.addedSafesSlice.reducer,
  [slices.settingsSlice.name]: slices.settingsSlice.reducer,
  [slices.cookiesAndTermsSlice.name]: slices.cookiesAndTermsSlice.reducer,
  [slices.popupSlice.name]: slices.popupSlice.reducer,
  [slices.spendingLimitSlice.name]: slices.spendingLimitSlice.reducer,
  [slices.safeAppsSlice.name]: slices.safeAppsSlice.reducer,
  [slices.pendingSafeMessagesSlice.name]: slices.pendingSafeMessagesSlice.reducer,
  [slices.batchSlice.name]: slices.batchSlice.reducer,
  [slices.undeployedSafesSlice.name]: slices.undeployedSafesSlice.reducer,
  [slices.pendingCfDeletesSlice.name]: slices.pendingCfDeletesSlice.reducer,
  [slices.swapParamsSlice.name]: slices.swapParamsSlice.reducer,
  [slices.visitedSafesSlice.name]: slices.visitedSafesSlice.reducer,
  [slices.orderByPreferenceSlice.name]: slices.orderByPreferenceSlice.reducer,
  [slices.hnStateSlice.name]: slices.hnStateSlice.reducer,
  [slices.hnQueueAssessmentsSlice.name]: slices.hnQueueAssessmentsSlice.reducer,
  [slices.calendlySlice.name]: slices.calendlySlice.reducer,
  [slices.globalSearchSlice.name]: slices.globalSearchSlice.reducer,
  [slices.safeActionsModalSlice.name]: slices.safeActionsModalSlice.reducer,
  [ofacApi.reducerPath]: ofacApi.reducer,
  [safePassApi.reducerPath]: safePassApi.reducer,
  [hypernativeApi.reducerPath]: hypernativeApi.reducer,
  [slices.gatewayApi.reducerPath]: slices.gatewayApi.reducer,
  [cgwClient.reducerPath]: cgwClient.reducer,
  [slices.authSlice.reducerPath]: slices.authSlice.reducer,
})

const persistedSlices: (keyof Partial<RootState>)[] = [
  slices.sessionSlice.name,
  slices.addressBookSlice.name,
  slices.pendingTxsSlice.name,
  slices.addedSafesSlice.name,
  slices.settingsSlice.name,
  slices.cookiesAndTermsSlice.name,
  slices.safeAppsSlice.name,
  slices.pendingSafeMessagesSlice.name,
  slices.batchSlice.name,
  slices.undeployedSafesSlice.name,
  slices.pendingCfDeletesSlice.name,
  slices.swapParamsSlice.name,
  slices.swapOrderSlice.name,
  slices.visitedSafesSlice.name,
  slices.orderByPreferenceSlice.name,
  slices.authSlice.name,
  slices.hnStateSlice.name,
]

export const getPersistedState = () => {
  return getPreloadedState(persistedSlices)
}

export const listenerMiddlewareInstance = createListenerMiddleware<RootState>()

const middleware: Middleware<{}, RootState>[] = [
  persistState(persistedSlices),
  broadcastState(persistedSlices),
  listenerMiddlewareInstance.middleware,
  ofacApi.middleware,
  safePassApi.middleware,
  hypernativeApi.middleware,
  slices.gatewayApi.middleware,
]

const listeners = [
  safeMessagesListener,
  txHistoryListener,
  txQueueListener,
  swapOrderListener,
  swapOrderStatusListener,
  authListener,
  counterfactualSyncListener,
]

export const _hydrationReducer: typeof rootReducer = (state, action) => {
  if (action.type === hydrate.HYDRATE_ACTION) {
    /**
     * When changing the schema of a Redux slice, previously stored data in LS might become incompatible.
     * To avoid this, we should always migrate the data on a case-by-case basis in the corresponding slice.
     * However, as a catch-all measure, we attempt to merge the stored data with the initial Redux state,
     * so that any newly added properties in the initial state are preserved, and existing properties are taken from the LS.
     *
     * @see https://lodash.com/docs/4.17.15#merge
     */
    const nextState = merge({}, state, action.payload) as RootState

    // Check if termsVersion matches
    if (nextState[cookiesAndTermsSlice.name] && nextState[cookiesAndTermsSlice.name].termsVersion !== termsVersion) {
      // Reset consent
      nextState[cookiesAndTermsSlice.name] = {
        ...cookiesAndTermsInitialState,
      }
    }

    // Migrate batchSlice txDetails to txData
    if (nextState[slices.batchSlice.name]) {
      nextState[slices.batchSlice.name] = migrateBatchTxs(nextState[slices.batchSlice.name])
    }

    // Mark the store as hydrated so guards wait for persisted auth state.
    // Reset cfSafeSynced so consumers wait for a fresh backend sync each page load.
    // Reset isOidcLoginPending to avoid stale state from a previous session.
    nextState.auth = {
      ...nextState.auth,
      isStoreHydrated: true,
      cfSafeSynced: false,
      isOidcLoginPending: false,
    }

    return nextState
  }
  return rootReducer(state, action) as RootState
}

/**
 * Build preloadedState for the RTK Query cache with build-time chain data.
 * This is purely synchronous — both SSR and client start with identical fulfilled
 * state, so no hydration mismatch can occur (unlike upsertQueryData which uses
 * microtasks that resolve differently on server vs client).
 */
const getStaticChainsPreloadedState = (): Partial<RootState> | undefined => {
  if (staticChainsData.length === 0) return undefined

  return {
    [cgwClient.reducerPath]: {
      queries: {
        [`getChainsConfigV2("${CONFIG_SERVICE_KEY}")`]: {
          status: 'fulfilled' as const,
          endpointName: 'getChainsConfigV2' as const,
          requestId: `static-chains-${Date.now()}`,
          originalArgs: CONFIG_SERVICE_KEY,
          startedTimeStamp: Date.now(),
          data: chainsAdapter.setAll(chainsInitialState, staticChainsData),
          fulfilledTimeStamp: Date.now(),
          error: undefined,
        },
      },
      mutations: {},
      provided: { tags: {}, keys: {} },
      subscriptions: {},
      config: {
        online: true,
        focused: true,
        middlewareRegistered: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
        refetchOnMountOrArgChange: false,
        keepUnusedDataFor: 60,
        reducerPath: cgwClient.reducerPath,
        invalidationBehavior: 'delayed',
      },
    },
  } as unknown as Partial<RootState>
}

type MakeStoreOptions = {
  skipBroadcast?: boolean
}
export const makeStore = (initialState?: Partial<RootState>, options?: MakeStoreOptions) => {
  setBaseUrl(GATEWAY_URL)

  // Merge user-provided initial state with build-time chains cache
  const chainsPreload = getStaticChainsPreloadedState()
  const mergedInitialState = chainsPreload ? { ...chainsPreload, ...initialState } : initialState

  const store = configureStore({
    reducer: _hydrationReducer,
    middleware: (getDefaultMiddleware) => {
      listeners.forEach((listener) => listener(listenerMiddlewareInstance))
      return getDefaultMiddleware({ serializableCheck: false }).concat(cgwClient.middleware).concat(middleware)
    },
    devTools: !IS_PRODUCTION,
    preloadedState: mergedInitialState,
  })

  if (!options?.skipBroadcast) {
    listenToBroadcast(store)
  }

  setupListeners(store.dispatch)

  return store
}

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof makeStore>
export type AppDispatch = AppStore['dispatch']
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action>

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const useHydrateStore = hydrate.useHydrateStore

// Store instance for imperative usage outside of React components
// This is initialized in _app.tsx and should be used for non-component contexts
let _store: ReturnType<typeof makeStore> | null = null

export const setStoreInstance = (store: ReturnType<typeof makeStore>) => {
  _store = store
}

export const getStoreInstance = () => {
  if (!_store) {
    throw new Error('Store not initialized. Ensure _app.tsx has called setStoreInstance.')
  }
  return _store
}

/**
 * Trigger a background refetch of chain configurations so the app picks up any
 * changes since the build-time snapshot. The static chain data is already seeded
 * into the RTK Query cache via preloadedState in makeStore(), so
 * useGetChainsConfigV2Query() returns data immediately on first render.
 *
 * When the runtime response arrives, RTK Query's structuralSharing preserves object
 * references if the data is identical, preventing unnecessary re-renders.
 */
export const useInitStaticChains = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    const result = dispatch(
      apiSliceWithChainsConfig.endpoints.getChainsConfigV2.initiate(CONFIG_SERVICE_KEY, { forceRefetch: true }),
    )

    return result.unsubscribe
  }, [dispatch])
}
